import { isEmpty } from 'lodash';

function findKeys(pickFrom, section, wildcard) {
  if (section === wildcard) {
    return Object.keys(pickFrom);
  }

  const wildcardIdx = section.indexOf(wildcard);
  if (wildcardIdx < 0) {
    return [section];
  }

  const prefix = section.substring(0, wildcardIdx);
  const suffix = section.substring(wildcardIdx + 1);
  return Object.keys(pickFrom).filter(k => {
    return k.startsWith(prefix) && k.endsWith(suffix);
  });
}

// eslint-disable-next-line complexity
function deepPickInner(
  pickFrom,
  path,
  pickTo = {},
  pathOffset = 0,
  ensureObjectPath = true,
  separator = '.',
  wildcard = '*'
) {
  const nextDelimeter = path.indexOf(separator, pathOffset);
  const section = path.substring(
    pathOffset,
    nextDelimeter >= 0 ? nextDelimeter : undefined
  );
  const keys = findKeys(pickFrom, section, wildcard);

  const isLastElement = pathOffset + section.length === path.length;
  if (isLastElement) {
    for (const key of keys) {
      if (!pickFrom.hasOwnProperty(key)) continue; // stop searching if there's no element in the src object
      pickTo[key] = pickFrom[key];
    }
  } else {
    // Non-wildcard prefix should always exist in pickTo
    const hasNotSeenWildcard = ensureObjectPath && !section.includes(wildcard);
    for (const key of keys) {
      // When the inner value is unable to be traversed, pretend it is an object
      const pickFromNext =
        typeof pickFrom[key] !== 'object' ? {} : pickFrom[key];

      if (!pickTo.hasOwnProperty(key)) {
        // Support `*.query` paths without unnecessarily creating an object
        const possiblyMutated = {};
        deepPickInner(
          pickFromNext,
          path,
          possiblyMutated,
          pathOffset + section.length + 1,
          hasNotSeenWildcard
        );
        if (!isEmpty(possiblyMutated) || hasNotSeenWildcard) {
          pickTo[key] = possiblyMutated;
        }
      } else {
        deepPickInner(
          pickFromNext,
          path,
          pickTo[key],
          pathOffset + section.length + 1,
          hasNotSeenWildcard
        );
      }
    }
  }
  return pickTo;
}

/**
 * This is similar to the lodash `pick` but with a distinction:
 * It handles deep properties / wildcards.
 *
 * If you're picking from an object like this:
 * {
 *   stuff: {
 *     specific: 'a',
 *     unrelated: 5
 *   },
 *   stuff2: {
 *     unrelated: 'b'
 *   },
 *   stuff3: {
 *     specific: 'c',
 *     unrelated: 4
 *   }
 * }
 *
 * and pass in the argument `*.specific`, the picked object will be:
 * {
 *   stuff: {
 *     specific: 'a'
 *   },
 *   stuff3: {
 *     specific: 'c'
 *   }
 * }
 *
 * @param {Object} pickFrom - Object to pick from
 * @param {Array}  paths - An array of strings
 * @returns {Object} - The final picked object
 */
export function deepPick(pickFrom, paths) {
  if (pickFrom === undefined) return {};

  const pickTo = {};
  for (const path of paths) {
    deepPickInner(pickFrom, path, pickTo);
  }
  return pickTo;
}

export default deepPick;
